import React, { useContext, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { BreadcrumbItemProps } from '../../../../../shared/components/Breadcrumbs/BreadcrumbItem/BreadcrumbItem';
import Breadcrumbs from '../../../../../shared/components/Breadcrumbs/Breadcrumbs';
import PageCurvedContainer from '../../../../../shared/components/PageCurvedContainer/PageCurvedContainer';
import StyledButtonNE from '../../../../../shared/components/StyledButtonNE/StyledButtonNE';
import { REWARDS_HISTORY_ROUTE } from '../../../../../shared/constants/auth';
import {
  CLICK_ACTION,
  POINTS_FILE_SKIP_AND_END,
  POINTS_REWARDS_MISSING_COLLABORATORS_FILE
} from '../../../../../shared/constants/tagManager';
import { RewardsContext } from '../../../../../shared/context/rewards/rewardsContext';
import { IRewardsFileValidation } from '../../../../../shared/interfaces/rewards';
import TagManagerService from '../../../../../shared/services/gtm/tagManagerService';
import { TRANSLATION_POINTS_BY_FILE } from '../../../../../shared/translations/rewards';
import { toLocaleNumberFormat } from '../../../../../shared/utils/utils';
import ShippingDetailCard from '../ShippingDetailCard/ShippingDetailCard';
import ReminderCard from './ReminderCard/ReminderCard';
import SuccessCard from './SuccessCard/SuccessCard';

interface PartialSuccessProps {
  previousPointsInfo: IRewardsFileValidation;
  orderNumber: string;
  sendPoints: (base64File: string, pointsInfo: IRewardsFileValidation) => Promise<void>;
}

const PartialSuccess = ({ previousPointsInfo, orderNumber, sendPoints }: PartialSuccessProps) => {
  const [validatedData, setValidatedData] = useState<IRewardsFileValidation>();
  const [base64, setBase64] = useState<string>();
  const [loading, setLoading] = useState<boolean>(false);

  const { balance } = useContext(RewardsContext);
  const navigate = useNavigate();
  const { t } = useTranslation();

  const pointsBreadcrumb: BreadcrumbItemProps[] = [
    { label: t(TRANSLATION_POINTS_BY_FILE.header.breadcrumb.rewards) },
    { label: t(TRANSLATION_POINTS_BY_FILE.header.breadcrumb.sendPoints) }
  ];

  const getValidatedData = (pointsInfo: IRewardsFileValidation | undefined) => {
    setValidatedData(pointsInfo);
  };

  const getFileData = (base64File: string | undefined) => {
    setBase64(base64File);
  };

  const onSendPoints = async () => {
    if (base64 && validatedData) {
      setLoading(true);
      await sendPoints(base64, validatedData);
      resetStates();
      setLoading(false);
      TagManagerService.pushEvent(CLICK_ACTION, POINTS_REWARDS_MISSING_COLLABORATORS_FILE);
    }
  };

  const resetStates = () => {
    setValidatedData(undefined);
    setBase64(undefined);
    setLoading(false);
  };

  const goToHistory = () => {
    TagManagerService.pushEvent(CLICK_ACTION, POINTS_FILE_SKIP_AND_END);
    navigate(REWARDS_HISTORY_ROUTE);
  };

  const hasBalance = () => {
    if (!validatedData?.hasBalance) {
      return false;
    }
    return balance - previousPointsInfo.totalAmount - validatedData.totalAmount >= 0;
  };

  const getMissingBalance = () => {
    return toLocaleNumberFormat(Number(validatedData?.totalAmount) + Number(previousPointsInfo.totalAmount) - balance);
  };

  return (
    <div className="page-container curved" data-experience="new">
      <div className="page-content-container">
        <Breadcrumbs items={pointsBreadcrumb} />
      </div>
      <PageCurvedContainer>
        <>
          <div className="flex gap-6">
            <SuccessCard orderNumber={orderNumber} />
            <ReminderCard
              setValidatedData={getValidatedData}
              setFileInfo={getFileData}
              previousPointsInfo={previousPointsInfo}
            />
          </div>

          {validatedData && (
            <ShippingDetailCard
              currentPointsInfo={validatedData}
              hasBalance={hasBalance()}
              missingBalance={getMissingBalance()}
            />
          )}

          <div className="self-end flex">
            <StyledButtonNE buttonType="tertiary" buttonSize="md" action={goToHistory}>
              {t(TRANSLATION_POINTS_BY_FILE.successWithErrors.buttons.skip)}
            </StyledButtonNE>
            {validatedData && (
              <StyledButtonNE
                buttonType="primary"
                buttonSize="md"
                action={onSendPoints}
                loading={loading}
                disabled={!validatedData || !base64 || !hasBalance()}
              >
                {t(TRANSLATION_POINTS_BY_FILE.successWithErrors.buttons.missingPoints)}
              </StyledButtonNE>
            )}
          </div>
        </>
      </PageCurvedContainer>
    </div>
  );
};

export default PartialSuccess;
